import {
  ReactNode,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react';
import React from 'react';

import type { LucideIcon } from 'lucide-react';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Search,
  X,
} from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import EmptyState from '@/components/ui/empty-state';
import StatusIndicator from '@/components/ui/status-indicator';
import Text from '@/components/ui/text';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  SortConfig,
  UseTableOptions,
  useTable,
} from '@/components/ui/use-table';
import { Card } from '@/components/ui/card';
import { useDebounce } from '@/hooks/use-debounce';
import { Size, getIconPixels } from '@/theme/size-tokens';
import { ColorVariant } from '@/theme/tokens';

type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>;

/** Interactive element tags that should not trigger onRowClick callback */
const INTERACTIVE_TAGS = new Set([
  'A',
  'BUTTON',
  'INPUT',
  'SELECT',
  'TEXTAREA',
  'LABEL',
]);

/** Check if an element or any of its ancestors (up to container) is interactive - used for onRowClick only */
function isInteractiveElement(
  element: HTMLElement | null,
  container: HTMLElement | null
): boolean {
  let current = element;
  while (current && current !== container) {
    if (INTERACTIVE_TAGS.has(current.tagName)) return true;
    if (current.getAttribute('role') === 'button') return true;
    if (current.getAttribute('role') === 'link') return true;
    if (current.hasAttribute('data-interactive')) return true;
    current = current.parentElement;
  }
  return false;
}

const INTERACTIVE_POINTER_CLASS =
  'pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_input]:pointer-events-auto [&_select]:pointer-events-auto [&_textarea]:pointer-events-auto [&_label]:pointer-events-auto [&_[data-interactive]]:pointer-events-auto [&_[role="button"]]:pointer-events-auto [&_[role="link"]]:pointer-events-auto';

export type DataGridAction<T> = {
  /** Icon name for the action button */
  icon: LucideIcon;
  /** Size of the icon */
  iconSize?: Size;
  /** Color variant for the icon */
  iconColor?: ColorVariant;
  /** Icon to display on hover (optional) */
  hoverIcon?: LucideIcon;
  /** Accessible label for the action (shown in tooltip or screen readers) */
  label: string;
  /** Tooltip text to display on hover. If not provided, label will be used for aria-label only. */
  tooltip?: string;
  /** Callback when the action is clicked */
  onClick: (row: T, index: number) => void;
  /**
   * Marks the button aria-disabled and ignores clicks. It stays focusable, so
   * keyboard users can reach the tooltip that says why.
   */
  disabled?: boolean;
  /** Visual variant for the action button */
  variant?:
    | 'primary'
    | 'teal'
    | 'neutral'
    | 'success'
    | 'warning'
    | 'error'
    | 'info';
};

export type DataGridActionItem<T> = DataGridAction<T> | ReactNode;

export type DataGridColumnDef<T> = {
  /** Key of the data property to display in this column */
  key: keyof T;
  /** Column header label */
  label: string;
  /** Whether this column can be sorted */
  sortable?: boolean;
  /** Custom sort value accessor. Return a string or number to use instead of the raw cell value when sorting. */
  sortValue?: (row: T) => string | number | null | undefined;
  /** CSS grid track value for the column (e.g., '200px', 'fit-content(24rem)', 'minmax(14rem, 1fr)') */
  width?: string;
  /**
   * Minimum width in pixels for the column. When set:
   * - Column truncates but never shrinks below this width
   * - Column auto-hides if container can't fit it at this width
   * - No need to also set hidePriority (though you can for explicit hide ordering)
   *
   * @example minWidth: 120 // Hide if email would be squished below 120px
   */
  minWidth?: number;
  /**
   * Maximum width in pixels for the column. Prevents column from growing too wide.
   * Use for columns like status/role that don't need much space.
   *
   * @example maxWidth: 100 // Cap role column at 100px
   */
  maxWidth?: number;
  /** Text alignment for cells in this column */
  align?: 'left' | 'center' | 'right';
  /** Whether text should wrap instead of truncating (useful for long strings) */
  wrapText?: boolean;
  /** Optional priority for responsive hiding. Lower values hide first when container width shrinks. Columns without hidePriority or minWidth are never hidden. */
  hidePriority?: number;
  /** Whether to show this column's label in mobile/vertical layout (default: true) */
  showLabelOnMobile?: boolean;
  /**
   * Marks this cell as interactive so custom rendered content stays above rowHref
   * overlays and captures pointer events.
   */
  interactive?: boolean;
  /** Custom render function for cell content (use as escape hatch when config options don't cover your use case) */
  render?: (value: T[keyof T], row: T, index: number) => ReactNode;

  // Config-driven rendering options (preferred over custom render)
  /** Pre-built formatter for common data types */
  format?: 'date' | 'currency' | 'number' | 'percent';
  /** Options for Intl formatters (NumberFormat or DateTimeFormat) */
  formatOptions?: Intl.NumberFormatOptions | Intl.DateTimeFormatOptions;
  /** Map values to badge variants. Unmatched values render with 'neutral' variant. Use '*' key to override the default fallback. */
  badgeMap?: {
    [key: string]: { variant?: ColorVariant; label?: string };
  };
  /** Map values to status dot colors. Renders text with a colored dot to the left. Lighter alternative to badges for status indicators. */
  statusDotMap?: {
    [key: string]: { color?: ColorVariant; label?: string };
  };
  /**
   * Render cell values as badges. When the value is an array, each item is rendered
   * as an individual badge; for scalar values, the value is wrapped in a single badge.
   */
  renderAsBadges?: boolean;
  /** Variant for badge rendering */
  badgeVariant?: ColorVariant;
  /** Size for badge rendering */
  componentSize?: 'sm' | 'md' | 'lg';
  /** Fallback value to display when cell value is null, undefined, or empty string */
  fallback?: string;
  /** Space after this column (right margin). Controls gap between this column and the next. */
  spacing?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  /** Whether this column can be resized by dragging. Inherits from the grid-level `resizable` prop when not set. */
  resizable?: boolean;
};

export type DataGridProps<T> = {
  /** Array of data objects to display in the grid */
  data: T[];
  /** Column definitions specifying how to render each column */
  columns: DataGridColumnDef<T>[];
  /** Additional CSS classes to apply */
  className?: string;
  /** Optional left inset for the grid content container */
  contentInset?: 'none' | 'sm' | 'md';
  /** Optional right inset for the actions column */
  actionInset?: 'none' | 'xs' | 'sm' | 'md';
  /** Gap between columns in grid mode */
  columnGap?: 'none' | 'xs' | 'sm' | 'md' | 'lg';

  // Styling options
  /** Whether to apply striped row styling (alternating background) */
  striped?: boolean;
  /** Whether rows have hover effects */
  hoverable?: boolean;
  /** Whether to use compact spacing for dense data */
  compact?: boolean;

  // Features
  /** Whether columns can be sorted (can be overridden per column) */
  sortable?: boolean;
  /** Whether to enable client-side pagination */
  paginated?: boolean;
  /** Number of rows per page when pagination is enabled */
  pageSize?: number;
  /** Whether to show checkboxes for row selection */
  selectable?: boolean;
  /** Whether to enable search/filter functionality */
  searchable?: boolean;
  /** Specific keys to search within. If not provided, searches all fields. */
  searchKeys?: (keyof T)[];
  /** Controlled search query. When provided, search input value is fully controlled by parent state. */
  searchQuery?: string;
  /** Placeholder text for the search input */
  searchPlaceholder?: string;
  /** URL search param key for persisting search state. Defaults to 'q'. When provided, search query is automatically synced to URL with 300ms debounce. */
  urlSearchParamKey?: string;
  /** Callback when search query changes */
  onSearchChange?: (query: string) => void;

  // Actions - either array of action configs/components OR function returning actions/render
  /** Action buttons, function returning actions based on row data, or custom render function for the actions column */
  actions?:
    | DataGridActionItem<T>[]
    | ((row: T, index: number) => DataGridActionItem<T>[] | ReactNode);
  /** Label for the actions column header */
  actionsLabel?: string;

  // Row link - makes entire row clickable for navigation
  /** Function returning href for row navigation. When provided, entire row becomes a link. */
  rowHref?: (row: T, index: number) => string | undefined;

  // Callbacks
  /** Callback when a row is clicked (if rowHref not provided) */
  onRowClick?: (row: T, index: number) => void;
  /** Callback when row selection changes (provides array of selected row indices) */
  onSelectionChange?: (selectedIndices: number[]) => void;

  // Empty state
  emptyState?: {
    /** Message to display when there is no data */
    title?: string;
    /** Description to display when there is no data */
    description?: string;
    /** Text for the button in the empty state */
    buttonText?: string;
    /** Click handler for the button in the empty state */
    onButtonClick?: () => void;
    /** React Router path — renders the empty state button as a <Link>. Use instead of onButtonClick for in-app navigation. */
    buttonTo?: string;
    /** Plain URL — renders the empty state button as an <a> tag. Use for external links. */
    buttonHref?: string;
    /** Link target (e.g. "_blank") — only used with buttonTo or buttonHref */
    buttonTarget?: React.HTMLAttributeAnchorTarget;
    /** Rel attribute for the anchor (defaults to "noopener noreferrer" when buttonTarget="_blank") */
    buttonRel?: string;
  };

  // Initial state
  /** Initial sort configuration */
  initialSort?: { key: keyof T; direction: 'asc' | 'desc' };
  /** Controlled sort configuration. When provided, sorting state is controlled by parent state. */
  sortConfig?: SortConfig<T>;
  /** Callback fired when sort configuration changes. */
  onSortChange?: (sortConfig: SortConfig<T>) => void;
  /** URL search param key for persisting sort state. Defaults to 'sort'. When enabled, sort state is automatically synced to URL with 'sort' and 'order' params. */
  urlSortParamKey?: string;
  /** Title displayed above the grid (left side of header) */
  title?: string;
  /** Header actions displayed above the grid (right side of header). */
  headerActions?: HeaderAction[];

  // Responsive / list mode
  /**
   * Container width (px) below which the grid collapses into list mode.
   * Defaults to 480. Column hiding (via `hidePriority` / `minWidth`) still
   * runs for widths above this breakpoint.
   */
  listBreakpoint?: number;
  /**
   * Custom render function for list mode. When provided, each row is rendered
   * using this function instead of the default vertical-stack fallback.
   * Use this to create type-specific list layouts with the right fields,
   * actions, and visual hierarchy for the entity being displayed.
   *
   * @example
   * ```tsx
   * renderListItem={(row, index, { actions, isSelected, onToggleSelect }) => (
   *   <div className="flex items-center gap-3">
   *     <Avatar name={row.name} />
   *     <div className="flex-1 min-w-0">
   *       <Text weight="medium" className="truncate">{row.name}</Text>
   *       <Text size="sm" variant="muted" className="truncate">{row.email}</Text>
   *     </div>
   *     {actions}
   *   </div>
   * )}
   * ```
   */
  renderListItem?: (
    row: T,
    index: number,
    context: DataGridListItemContext
  ) => ReactNode;
  /** Whether columns can be resized by dragging their right edge. AG Grid-style: single-column resize with horizontal scroll. */
  resizable?: boolean;
  /** Wraps the DataGrid in a Card for a contained, bordered, shadowed presentation */
  card?: boolean;
  /** Whether to make the column header row sticky so it remains visible when the grid scrolls vertically */
  stickyHeader?: boolean;
  /**
   * Server-side pagination config. When provided the grid renders its own Prev/Next controls
   * but delegates navigation to the caller instead of slicing data client-side.
   * Pass the current page's items as `data`. The grid will display them without further slicing.
   */
  serverPagination?: {
    canNextPage: boolean;
    canPrevPage: boolean;
    onNextPage: () => void;
    onPrevPage: () => void;
    isFetching?: boolean;
    /** Human-readable page indicator shown in the footer (e.g. "Page 2") */
    pageLabel?: string;
  };
};

export type HeaderAction = {
  /** Button text; if omitted and `icon` provided, button will be icon-only */
  label?: string;
  /** Icon name to render inside the button (maps to library `Icon`) */
  icon?: LucideIcon;
  /** Accessible label for screen readers. Required for icon-only buttons (when label is omitted). */
  ariaLabel?: string;
  /** Click handler invoked when the button is clicked */
  onClick?: () => void;
  /** React Router path — renders the button as a <Link>. Use instead of onClick for in-app navigation. */
  to?: string;
  /** Plain URL — renders the button as an <a> tag. Use for external links. */
  href?: string;
  /** Link target (e.g. "_blank") — only used with to or href */
  target?: React.HTMLAttributeAnchorTarget;
  /** Rel attribute for the anchor (defaults to "noopener noreferrer" when target="_blank") */
  rel?: string;
  /** Button variant */
  variant?: string;
  /** Button style — mapped to variant */
  style?: string;
  /** Button size */
  size?: 'default' | 'sm' | 'lg' | 'xs';
  /** Additional className forwarded to the Button */
  className?: string;
  /** Disable the button */
  disabled?: boolean;
  /** Tooltip text shown on hover (useful when disabled) */
  tooltip?: string;
};

/** Breakpoint width (in px) below which we switch to list mode */
const DEFAULT_LIST_BREAKPOINT = 480;
/** Extra buffer (px) required before switching back from list mode to grid mode */
const LIST_MODE_HYSTERESIS = 12;
/** Extra buffer (px) before showing an additional hideable column */
const COLUMN_VISIBILITY_HYSTERESIS = 12;
const PX_PER_REM = 16;

/** Map legacy variant + style combos to shadcn button variants */
function resolveButtonVariant(variant?: string, style?: string): string {
  if (variant === 'primary' && (!style || style === 'solid')) return 'default';
  if (variant === 'primary' && style === 'ghost') return 'ghost-primary';
  if (variant === 'primary' && style === 'outline') return 'outline-primary';
  if (variant === 'primary' && style === 'link') return 'link-primary';
  if (variant === 'error' && (!style || style === 'solid'))
    return 'destructive';
  if (variant === 'error' && style === 'ghost') return 'ghost-error';
  if (variant === 'error' && style === 'outline') return 'outline-error';
  if (variant === 'neutral' && style === 'ghost') return 'ghost';
  if (variant === 'neutral' && style === 'outline') return 'outline';
  if (variant === 'neutral' && style === 'link') return 'link';
  if (!variant && style === 'outline') return 'outline';
  if (!variant && style === 'ghost') return 'ghost';
  if (!variant && style === 'link') return 'link';
  return variant ?? 'default';
}

function renderHeaderAction(act: HeaderAction, idx: number) {
  const ActIcon = act.icon;
  const buttonProps = {
    variant: resolveButtonVariant(act.variant, act.style) as ButtonVariant,
    size: act.size,
    className: act.className ?? '',
    'aria-label':
      act.ariaLabel ?? (!act.label && act.icon ? 'Action' : undefined),
    disabled: act.disabled,
  };

  let button: React.ReactNode;

  if (act.to) {
    button = (
      <Button key={idx} {...buttonProps} asChild>
        <Link to={act.to}>
          {ActIcon && <ActIcon data-icon="inline-start" />}
          {act.label}
        </Link>
      </Button>
    );
  } else if (act.href) {
    const rel =
      act.rel ?? (act.target === '_blank' ? 'noopener noreferrer' : undefined);
    button = (
      <Button key={idx} {...buttonProps} asChild>
        <a href={act.href} target={act.target} rel={rel}>
          {ActIcon && <ActIcon data-icon="inline-start" />}
          {act.label}
        </a>
      </Button>
    );
  } else {
    button = (
      <Button key={idx} {...buttonProps} onClick={act.onClick}>
        {ActIcon && <ActIcon data-icon="inline-start" />}
        {act.label}
      </Button>
    );
  }

  if (act.tooltip) {
    return (
      <TooltipProvider key={idx}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={act.disabled ? 0 : undefined}
              className={act.disabled ? 'cursor-not-allowed' : undefined}
            >
              {button}
            </span>
          </TooltipTrigger>
          <TooltipContent>{act.tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

function estimateTrackWidth(track: string): number {
  const normalizedTrack = track.trim();

  if (normalizedTrack === '1fr' || normalizedTrack === 'minmax(0, 1fr)') {
    return 180;
  }

  const pxMatches = [...normalizedTrack.matchAll(/(\d+(?:\.\d+)?)px/g)].map(
    (match) => Number(match[1])
  );
  if (pxMatches.length > 0) {
    return Math.max(...pxMatches);
  }

  const remMatches = [...normalizedTrack.matchAll(/(\d+(?:\.\d+)?)rem/g)].map(
    (match) => Number(match[1]) * PX_PER_REM
  );
  if (remMatches.length > 0) {
    return Math.max(...remMatches);
  }

  return 180;
}

function getDefaultMeasuredWidthCap<T>(
  column: DataGridColumnDef<T>
): number | undefined {
  if (column.maxWidth) {
    return column.maxWidth;
  }

  if (column.width?.trim().startsWith('fit-content(')) {
    return estimateTrackWidth(column.width);
  }

  const columnKey = String(column.key).toLowerCase();

  if (column.wrapText) {
    return 28 * PX_PER_REM;
  }

  if (
    columnKey === 'name' ||
    columnKey.endsWith('name') ||
    columnKey === 'email' ||
    columnKey.endsWith('email')
  ) {
    return 24 * PX_PER_REM;
  }

  return undefined;
}

function clampMeasuredWidth<T>(
  column: DataGridColumnDef<T>,
  measuredWidth: number
): number {
  let width = measuredWidth;

  if (column.minWidth) {
    width = Math.max(width, column.minWidth);
  }

  const maxWidth = getDefaultMeasuredWidthCap(column);
  if (maxWidth) {
    width = Math.min(width, maxWidth);
  }

  return width;
}

/**
 * Context passed to `renderListItem` so list-mode renders can embed grid actions
 * and selection state without needing to manage them independently.
 */
export type DataGridListItemContext = {
  /** Pre-rendered action buttons derived from the `actions` prop */
  actions: ReactNode;
  /** Whether this row is currently selected */
  isSelected: boolean;
  /** Toggle selection for this row. Defined only when `selectable` is true. */
  onToggleSelect?: () => void;
};

/**
 * DataGrid - Responsive data display component with Vercel-style layout
 *
 * Features:
 * - Generic typed data and columns (same API as DataTable)
 * - Responsive layout: horizontal rows on desktop, vertical card-like on mobile
 * - Clickable rows via rowHref with interactive elements taking priority
 * - Client-side sorting, pagination, selection, and search
 * - Custom cell rendering with DataGridCell for multi-element stacking
 *
 * @example
 * ```tsx
 * <DataGrid
 *   data={deployments}
 *   columns={[
 *     {
 *       key: 'name',
 *       label: 'Deployment',
 *       render: (value, row) => (
 *         <DataGridCell>
 *           <Text weight="medium">{row.name}</Text>
 *           <Text size="sm" variant="muted">{row.url}</Text>
 *         </DataGridCell>
 *       )
 *     },
 *     { key: 'status', label: 'Status', badgeMap: { ready: { variant: 'success' } } },
 *     { key: 'createdAt', label: 'Created', format: 'date' }
 *   ]}
 *   rowHref={(row) => `/deployments/${row.id}`}
 *   hoverable
 * />
 * ```
 */
function DataGrid<T>({
  data,
  columns,
  className = '',
  contentInset = 'md',
  actionInset = 'sm',
  columnGap = 'md',
  striped = false,
  hoverable = true,
  compact = false,
  sortable = true,
  paginated = false,
  pageSize = 10,
  selectable = false,
  searchable = false,
  searchQuery: controlledSearchQuery,
  searchKeys,
  searchPlaceholder = 'Search...',
  urlSearchParamKey = 'q',
  onSearchChange,
  actions,
  actionsLabel = 'Actions',
  rowHref,
  onRowClick,
  onSelectionChange,
  emptyState = {
    title: 'No data available',
    description: undefined,
    buttonText: undefined,
    onButtonClick: undefined,
    buttonTo: undefined,
    buttonHref: undefined,
    buttonTarget: undefined,
    buttonRel: undefined,
  },
  headerActions,
  title,
  initialSort,
  sortConfig: controlledSortConfig,
  onSortChange,
  urlSortParamKey = 'sort',
  listBreakpoint = DEFAULT_LIST_BREAKPOINT,
  renderListItem,
  resizable = false,
  card = false,
  stickyHeader = false,
  serverPagination,
}: DataGridProps<T>) {
  // Validate edge cases - warn but don't crash
  if (!Array.isArray(columns) || columns.length === 0) {
    console.warn('DataGrid: columns must be a non-empty array');
  }
  if (paginated && pageSize <= 0) {
    console.warn(
      'DataGrid: pageSize must be greater than 0 when paginated is true'
    );
  }

  // Handle URL search param persistence if enabled
  const [searchParams, setSearchParams] = useSearchParams();
  const [internalSearchQuery, setInternalSearchQuery] = useState(() =>
    urlSearchParamKey ? (searchParams.get(urlSearchParamKey) ?? '') : ''
  );
  const debouncedSearchQuery = useDebounce(internalSearchQuery, 300);

  useEffect(() => {
    if (!urlSearchParamKey) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearchQuery.trim()) {
          next.set(urlSearchParamKey, debouncedSearchQuery);
        } else {
          next.delete(urlSearchParamKey);
        }
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearchQuery, urlSearchParamKey, setSearchParams]);

  const searchQuery = controlledSearchQuery ?? internalSearchQuery;
  const deferredSearchQuery = useDeferredValue(searchQuery);

  // Handle URL sort param persistence if enabled
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig<T>>(
    () => {
      if (controlledSortConfig) {
        return controlledSortConfig;
      }

      if (!urlSortParamKey) {
        return initialSort
          ? { key: initialSort.key, direction: initialSort.direction }
          : null;
      }

      const sortKey = searchParams.get(urlSortParamKey);
      const sortOrder = searchParams.get('order');

      if (!sortKey || (sortOrder !== 'asc' && sortOrder !== 'desc')) {
        return initialSort
          ? { key: initialSort.key, direction: initialSort.direction }
          : null;
      }

      // Try to find matching column
      const matchingColumn = columns.find((col) => String(col.key) === sortKey);
      if (!matchingColumn) {
        return initialSort
          ? { key: initialSort.key, direction: initialSort.direction }
          : null;
      }

      return {
        key: matchingColumn.key,
        direction: sortOrder,
      };
    }
  );

  useEffect(() => {
    if (!urlSortParamKey) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (internalSortConfig?.direction) {
          next.set(urlSortParamKey, String(internalSortConfig.key));
          next.set('order', internalSortConfig.direction);
        } else {
          next.delete(urlSortParamKey);
          next.delete('order');
        }
        return next;
      },
      { replace: true }
    );
  }, [internalSortConfig, urlSortParamKey, setSearchParams]);

  // Determine effective sort config and handler
  const effectiveSortConfig = controlledSortConfig ?? internalSortConfig;
  const handleSortChangeWithUrl = (nextSortConfig: SortConfig<T>) => {
    if (!controlledSortConfig) {
      setInternalSortConfig(nextSortConfig);
    }
    if (onSortChange) {
      onSortChange(nextSortConfig);
    }
  };

  // Layout state – true when container width is below listBreakpoint
  const [isListMode, setIsListMode] = useState(false);
  const isListModeRef = useRef(false);

  const handleSearchChange = (value: string) => {
    if (controlledSearchQuery === undefined) {
      setInternalSearchQuery(value);
    }

    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  // Container ref for responsive behavior
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<DataGridColumnDef<T>[]>(
    () => columns
  );
  const visibleColumnsRef = useRef<DataGridColumnDef<T>[]>(columns);
  const [measuredColumnWidths, setMeasuredColumnWidths] = useState<
    Record<string, number>
  >({});

  // Calculate minimum width needed for actions column based on max actions in any row
  const calculateActionsMinWidth = (): number => {
    if (!actions) return 0;

    let maxActionCount = 0;

    if (Array.isArray(actions)) {
      // Static actions - all rows have the same actions
      maxActionCount = actions.length;
    } else {
      // Dynamic actions - need to check all rows
      data.forEach((row, index) => {
        const result = actions(row, index);
        if (Array.isArray(result)) {
          maxActionCount = Math.max(maxActionCount, result.length);
        } else {
          // Custom render - assume it needs reasonable space (equivalent to 2 actions)
          maxActionCount = Math.max(maxActionCount, 2);
        }
      });
    }

    if (maxActionCount === 0) {
      // No actions, but we still need some minimum space in case actions appear
      return 60;
    }

    // Each action button takes ~36px (icon + button padding)
    // Stack spacing between actions is 8px (spacing="sm")
    // Formula: (actionWidth * count) + (gap * (count - 1)) + extraPadding
    const actionWidth = 24;
    const gapWidth = 8;
    const extraPadding = 16; // breathing room

    return (
      actionWidth * maxActionCount +
      gapWidth * Math.max(0, maxActionCount - 1) +
      extraPadding
    );
  };

  const actionsMinWidth = calculateActionsMinWidth();

  const getGridGapPixels = (
    gap: NonNullable<DataGridProps<T>['columnGap']>
  ): number => {
    switch (gap) {
      case 'none':
        return 0;
      case 'xs':
        return 4;
      case 'sm':
        return 8;
      case 'lg':
        return 24;
      case 'md':
      default:
        return 16;
    }
  };

  const getGridGapClass = (
    gap: NonNullable<DataGridProps<T>['columnGap']>
  ): string => {
    switch (gap) {
      case 'none':
        return 'gap-0';
      case 'xs':
        return 'gap-1';
      case 'sm':
        return 'gap-2';
      case 'lg':
        return 'gap-6';
      case 'md':
      default:
        return 'gap-4';
    }
  };

  const gridGapClass = getGridGapClass(columnGap);
  const gridGapPixels = getGridGapPixels(columnGap);

  // ResizeObserver to handle responsive column hiding and mobile layout
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Check if any columns have hidePriority or minWidth set (both make columns hideable)
    const hasHideableColumns = columns.some(
      (col) => col.hidePriority !== undefined || col.minWidth !== undefined
    );

    const setVisibleColumnsIfChanged = (next: DataGridColumnDef<T>[]) => {
      const current = visibleColumnsRef.current;
      const unchanged =
        current.length === next.length &&
        current.every((col, index) => col.key === next[index]?.key);

      if (!unchanged) {
        visibleColumnsRef.current = next;
        setVisibleColumns(next);
        // When responsive hiding changes the column set, clear fixed widths
        // so the grid falls back to flexible tracks and the snapshot effect
        // re-measures at natural sizes.
        if (resizable) {
          columnWidthsRef.current = [];
          setColumnWidths([]);
        }
      }
    };

    const updateLayout = () => {
      // Don't change column visibility while user is actively resizing
      if (resizeDragIdx.current !== null) return;

      const containerWidth = container.offsetWidth;

      // When the viewport width changes after a manual resize, reset to
      // flexible tracks so columns fill the available space naturally.
      if (
        resizable &&
        columnWidthsRef.current.length > 0 &&
        snapshotContainerWidth.current > 0 &&
        containerWidth !== snapshotContainerWidth.current
      ) {
        columnWidthsRef.current = [];
        setColumnWidths([]);
        snapshotContainerWidth.current = 0;
      }

      // Check if we should switch to list mode.
      // Use hysteresis to prevent flip-flopping around the breakpoint.
      let nowListMode = isListModeRef.current;
      if (nowListMode) {
        if (containerWidth > listBreakpoint + LIST_MODE_HYSTERESIS) {
          nowListMode = false;
        }
      } else if (containerWidth < listBreakpoint) {
        nowListMode = true;
      }

      if (nowListMode !== isListModeRef.current) {
        isListModeRef.current = nowListMode;
        setIsListMode(nowListMode);
      }

      // In list mode, show all columns (either renderListItem handles layout
      // or the fallback vertical stack renders them all with labels).
      // Only apply responsive column hiding in grid mode.
      if (nowListMode || !hasHideableColumns) {
        setVisibleColumnsIfChanged(columns);
        return;
      }

      // Columns are hideable if they have hidePriority OR minWidth
      const isHideable = (col: DataGridColumnDef<T>, index: number) => {
        // Keep the first data column always visible for stable identity context.
        if (index === 0) return false;
        return col.hidePriority !== undefined || col.minWidth !== undefined;
      };

      // Separate columns into hideable and non-hideable
      const hideableColumns = columns
        .map((col, index) => ({ col, originalIndex: index }))
        .filter(({ col, originalIndex }) => isHideable(col, originalIndex))
        .sort((a, b) => {
          // Sort by hidePriority if both have it (lower hides first)
          const aPriority = a.col.hidePriority;
          const bPriority = b.col.hidePriority;

          if (aPriority !== undefined && bPriority !== undefined) {
            if (aPriority !== bPriority) return aPriority - bPriority;
          }
          // Columns with explicit hidePriority hide before minWidth-only columns
          if (aPriority !== undefined && bPriority === undefined) return -1;
          if (aPriority === undefined && bPriority !== undefined) return 1;

          // For minWidth-only columns, larger minWidth hides first (needs more space)
          const aMin = a.col.minWidth ?? 0;
          const bMin = b.col.minWidth ?? 0;
          if (aMin !== bMin) return bMin - aMin;

          // Finally, later columns hide first (preserve leading columns)
          return b.originalIndex - a.originalIndex;
        });

      const nonHideableColumns = columns.filter(
        (col, index) => !isHideable(col, index)
      );

      // Estimate column width - use minWidth if set, otherwise use spacing-based minimum or parse width
      const estimateColumnWidthNatural = (
        col: DataGridColumnDef<T>
      ): number => {
        // Use minWidth if explicitly set (this is the minimum space the column needs)
        if (col.minWidth) return col.minWidth;

        if (col.width) {
          return estimateTrackWidth(col.width);
        }
        // Use spacing-based minimum, or fallback to 100
        return getMinimumFromSpacing(col.spacing);
      };

      // Live estimate: prefer actual rendered widths when available (for hide decisions)
      const estimateColumnWidthLive = (col: DataGridColumnDef<T>): number => {
        const liveWidths = columnWidthsRef.current;
        const currentVisible = visibleColumnsRef.current;
        if (resizable && liveWidths.length === currentVisible.length) {
          const visIdx = currentVisible.indexOf(col);
          if (visIdx >= 0 && visIdx < liveWidths.length) {
            return liveWidths[visIdx];
          }
        }
        return estimateColumnWidthNatural(col);
      };

      // Calculate horizontal padding (px-3 = 24px total, px-4 = 32px total)
      const horizontalPadding = compact ? 24 : 32;
      // Grid gap between each column (must stay in sync with rendered class)
      const gridGap = gridGapPixels;

      // Base width from non-hideable columns and utility tracks.
      let baseWidth = nonHideableColumns.reduce(
        (sum, col) => sum + estimateColumnWidthLive(col),
        0
      );
      let baseWidthNatural = nonHideableColumns.reduce(
        (sum, col) => sum + estimateColumnWidthNatural(col),
        0
      );
      let baseColumnCount = nonHideableColumns.length;

      if (selectable) {
        baseWidth += 48;
        baseWidthNatural += 48;
        baseColumnCount += 1;
      }
      if (actions) {
        baseWidth += actionsMinWidth;
        baseWidthNatural += actionsMinWidth;
        // The actions column, plus the spacer track buildGridTemplateColumns
        // puts before it once columns are measured: its gap takes width too.
        baseColumnCount += 2;
      }

      const hideableWidthsLive = hideableColumns.map(({ col }) =>
        estimateColumnWidthLive(col)
      );
      const hideableWidthsNatural = hideableColumns.map(({ col }) =>
        estimateColumnWidthNatural(col)
      );

      // Live total — uses actual rendered widths for hide decisions
      const totalWidthForHideableCountLive = (
        hideableCount: number
      ): number => {
        const hideableWidth = hideableWidthsLive
          .slice(0, hideableCount)
          .reduce((sum, width) => sum + width, 0);
        const totalColumns = baseColumnCount + hideableCount;
        const totalGaps = Math.max(0, totalColumns - 1) * gridGap;
        return baseWidth + hideableWidth + totalGaps + horizontalPadding;
      };

      // Natural total — uses config widths for show decisions (since showing
      // a column resets to natural layout anyway)
      const totalWidthForHideableCountNatural = (
        hideableCount: number
      ): number => {
        const hideableWidth = hideableWidthsNatural
          .slice(0, hideableCount)
          .reduce((sum, width) => sum + width, 0);
        const totalColumns = baseColumnCount + hideableCount;
        const totalGaps = Math.max(0, totalColumns - 1) * gridGap;
        return baseWidthNatural + hideableWidth + totalGaps + horizontalPadding;
      };

      // The rows sit inside the container's padding (content and action insets).
      const containerStyle = getComputedStyle(container);
      const rowWidth =
        container.clientWidth -
        (parseFloat(containerStyle.paddingLeft) || 0) -
        (parseFloat(containerStyle.paddingRight) || 0);

      // Start from current visibility and only change when we cross hysteresis bands.
      const visibleKeys = new Set(
        visibleColumnsRef.current.map((col) => String(col.key))
      );
      let visibleHideableCount = hideableColumns.reduce(
        (count, { col }) => count + (visibleKeys.has(String(col.key)) ? 1 : 0),
        0
      );
      visibleHideableCount = Math.min(
        visibleHideableCount,
        hideableColumns.length
      );

      // Hide when we're sufficiently below the current requirement.
      // Uses live widths — actual rendered sizes determine when overflow occurs.
      while (
        visibleHideableCount > 0 &&
        rowWidth <
          totalWidthForHideableCountLive(visibleHideableCount) -
            COLUMN_VISIBILITY_HYSTERESIS
      ) {
        visibleHideableCount -= 1;
      }

      // Show when we're sufficiently above the next requirement.
      // Uses natural/config widths — showing a column resets to natural layout,
      // so the decision should be based on natural sizes, not inflated live widths.
      while (
        visibleHideableCount < hideableColumns.length &&
        rowWidth >=
          totalWidthForHideableCountNatural(visibleHideableCount + 1) +
            COLUMN_VISIBILITY_HYSTERESIS
      ) {
        visibleHideableCount += 1;
      }

      const visibleHideable = new Set(
        hideableColumns.slice(0, visibleHideableCount).map(({ col }) => col)
      );

      // Restore original column order.
      const orderedVisibleColumns = columns.filter(
        (col, index) => !isHideable(col, index) || visibleHideable.has(col)
      );
      setVisibleColumnsIfChanged(orderedVisibleColumns);
    };

    // Call once on mount
    updateLayout();

    // Set up observer
    const observer = new ResizeObserver(() => {
      updateLayout();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [
    columns,
    selectable,
    actions,
    actionsMinWidth,
    compact,
    listBreakpoint,
    gridGapPixels,
    resizable,
  ]);

  // Use table hook for state management
  // IMPORTANT: Use original columns for search keys, not visibleColumns
  // visibleColumns changes on resize, which would break search when columns are hidden
  const effectiveSearchKeys = searchKeys ?? columns.map((col) => col.key);

  const tableOptions: UseTableOptions<T> = {
    data,
    initialSort,
    sortConfig: effectiveSortConfig,
    onSortChange: handleSortChangeWithUrl,
    pageSize,
    searchQuery: deferredSearchQuery,
    searchKeys: effectiveSearchKeys,
    getSortValue: (key, row) => {
      const col = columns.find((c) => c.key === key);
      if (col?.sortValue) return col.sortValue(row);
      const val = row[key];
      return val as string | number | null | undefined;
    },
  };

  const {
    currentData,
    sortedData,
    filteredData,
    sortConfig,
    handleSort,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    canNextPage,
    canPrevPage,
    selectedRows,
    toggleRow,
    toggleAll,
    isRowSelected,
    isAllSelected,
  } = useTable(tableOptions);

  // Display data:
  // - serverPagination: caller owns slicing, show all data as-is (sorted only)
  // - paginated: client-side slice via useTable
  // - else: all sorted data
  const displayData = serverPagination
    ? sortedData
    : paginated
      ? currentData
      : sortedData;

  // ── AG Grid-style column resize ──────────────────────────────────────
  // When `resizable` is true, columns use flexible tracks by default.
  // Dragging a resize handle snapshots current widths to fixed px and
  // switches to px tracks. Responsive column hiding clears widths back
  // to flexible so columns fill the available space naturally.
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const columnWidthsRef = useRef<number[]>([]);
  columnWidthsRef.current = columnWidths;
  const columnWidthsReady =
    resizable && columnWidths.length === visibleColumns.length;

  const resizeDragIdx = useRef<number | null>(null);
  const resizeDragStartX = useRef(0);
  const resizeDragStartWidths = useRef<number[]>([]);
  // Container width at the time column widths were snapshot. Used to detect
  // viewport changes and reset to flexible tracks.
  const snapshotContainerWidth = useRef<number>(0);

  // Snapshot current flexible layout into fixed px widths.
  // Called lazily on drag start — NOT eagerly on mount — so that
  // responsive re-show always returns to natural flexible tracks.
  const snapshotColumnWidths = useCallback((): number[] => {
    const container = containerRef.current;
    if (!container) return [];
    const widths: number[] = [];
    visibleColumns.forEach((col, i) => {
      const el = container.querySelector<HTMLElement>(
        `[data-col-idx="${i}"].header`
      );
      widths.push(el ? el.offsetWidth : (col.minWidth ?? 150));
    });
    return widths;
  }, [visibleColumns]);

  // Measured intrinsic widths of header content (label text + sort icon).
  // Used as the floor for column min-widths during resize.
  const headerMinWidthsRef = useRef<number[]>([]);

  const measureHeaderMinWidths = useCallback((): number[] => {
    const container = containerRef.current;
    if (!container) return [];
    const widths: number[] = [];
    visibleColumns.forEach((_, i) => {
      const headerEl = container.querySelector<HTMLElement>(
        `[data-col-idx="${i}"].header`
      );
      if (!headerEl) {
        widths.push(0);
        return;
      }
      const contentWrapper = headerEl.firstElementChild as HTMLElement | null;
      const contentEl = contentWrapper?.firstElementChild as HTMLElement | null;
      if (!contentEl) {
        widths.push(0);
        return;
      }

      let intrinsicWidth: number;
      if (contentEl.tagName === 'BUTTON') {
        // inline-flex button: getBCR width = intrinsic content width
        intrinsicWidth = contentEl.getBoundingClientRect().width;
      } else {
        // Block elements (e.g. <p>): use Range API to measure text content width
        const range = document.createRange();
        range.selectNodeContents(contentEl);
        intrinsicWidth = range.getBoundingClientRect().width;
      }

      const paddingRight =
        parseFloat(getComputedStyle(headerEl).paddingRight) || 0;
      const breathingRoom = 8;
      widths.push(Math.ceil(intrinsicWidth + paddingRight + breathingRoom));
    });
    return widths;
  }, [visibleColumns]);

  const handleResizeStart = useCallback(
    (colIdx: number, e: React.MouseEvent) => {
      e.preventDefault();
      resizeDragIdx.current = colIdx;
      resizeDragStartX.current = e.clientX;

      // If not yet in fixed-px mode, snapshot current flexible layout
      let widths = columnWidths;
      if (widths.length !== visibleColumns.length) {
        widths = snapshotColumnWidths();
        setColumnWidths(widths);
      }
      snapshotContainerWidth.current = containerRef.current?.offsetWidth ?? 0;
      resizeDragStartWidths.current = [...widths];
      headerMinWidthsRef.current = measureHeaderMinWidths();
    },
    [columnWidths, visibleColumns, snapshotColumnWidths, measureHeaderMinWidths]
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      const idx = resizeDragIdx.current;
      if (idx === null) return;
      const startWidths = resizeDragStartWidths.current;
      if (startWidths.length === 0) return;
      const delta = e.clientX - resizeDragStartX.current;
      const col = visibleColumns[idx];
      const min = Math.max(
        col?.minWidth ?? 50,
        headerMinWidthsRef.current[idx] ?? 50
      );
      const max = col?.maxWidth ?? Infinity;
      const rawDesired = Math.round(startWidths[idx] + delta);
      const desired = Math.min(max, Math.max(min, rawDesired));
      const change = desired - startWidths[idx];

      // Rebuild all columns from the drag-start snapshot so every frame
      // is computed from a stable baseline, not from accumulated state.
      const copy = [...startWidths];
      let remaining = change;

      // Cascade RIGHT: steal/give space from columns after the dragged one.
      for (let i = idx + 1; i < copy.length && remaining !== 0; i++) {
        const neighbor = visibleColumns[i];
        const nMin = Math.max(
          neighbor?.minWidth ?? 50,
          headerMinWidthsRef.current[i] ?? 50
        );
        const nMax = neighbor?.maxWidth ?? Infinity;
        const nNext = Math.min(nMax, Math.max(nMin, copy[i] - remaining));
        const absorbed = copy[i] - nNext;
        copy[i] = nNext;
        remaining -= absorbed;
      }

      // If the dragged column hit its limit and the user is still dragging,
      // cascade LEFT: shrink columns before the dragged one and give freed
      // space to the right side. Triggers on extraDelta instead of remaining
      // because the right cascade already absorbed the initial change.
      const extraDelta = rawDesired - desired;
      if (extraDelta !== 0) {
        let leftRemaining = extraDelta; // negative when dragging left past min

        for (let i = idx - 1; i >= 0 && leftRemaining !== 0; i--) {
          const neighbor = visibleColumns[i];
          const nMin = Math.max(
            neighbor?.minWidth ?? 50,
            headerMinWidthsRef.current[i] ?? 50
          );
          const nMax = neighbor?.maxWidth ?? Infinity;
          const nNext = Math.min(nMax, Math.max(nMin, copy[i] + leftRemaining));
          const absorbed = nNext - copy[i];
          copy[i] = nNext;
          leftRemaining -= absorbed;
        }

        // Space freed by left neighbors goes directly to right-side columns.
        const leftFreed = extraDelta - leftRemaining;
        if (leftFreed !== 0) {
          let rightExtra = leftFreed;
          for (let i = idx + 1; i < copy.length && rightExtra !== 0; i++) {
            const neighbor = visibleColumns[i];
            const nMin = Math.max(
              neighbor?.minWidth ?? 50,
              headerMinWidthsRef.current[i] ?? 50
            );
            const nMax = neighbor?.maxWidth ?? Infinity;
            const nNext = Math.min(nMax, Math.max(nMin, copy[i] - rightExtra));
            const absorbed = copy[i] - nNext;
            copy[i] = nNext;
            rightExtra -= absorbed;
          }
        }
      }

      const actual = desired - remaining;
      if (actual < min || actual === startWidths[idx]) return;
      copy[idx] = actual;

      setColumnWidths(copy);
    },
    [visibleColumns]
  );

  const handleResizeEnd = useCallback(() => {
    resizeDragIdx.current = null;
  }, []);

  // Attach global mousemove / mouseup while dragging
  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);
  // ── End resize ───────────────────────────────────────────────────────

  // Measure visible header/body cells and keep column widths in sync with the current dataset.
  // Skip when resizable — resizable mode uses fixed px widths from columnWidths[].
  useEffect(() => {
    if (isListMode || !containerRef.current || resizable) return;

    const measured = new Map<string, number>();

    visibleColumns.forEach((column) => {
      const key = String(column.key);
      const headerCells = containerRef.current?.querySelectorAll(
        `[data-col-key="${key}"].header`
      );
      const bodyCells = containerRef.current?.querySelectorAll(
        `[data-col-key="${key}"].body`
      );

      headerCells?.forEach((cell) => {
        const width = (cell as HTMLElement).scrollWidth;
        const current = measured.get(key) ?? 0;
        if (width > current) {
          measured.set(key, width);
        }
      });

      bodyCells?.forEach((cell) => {
        // Measure the inner content element, not the cell: the cell clips
        // (overflow-hidden) and its truncating child never overflows it, so the
        // cell's scrollWidth just echoes the current track width. The content
        // element's own scrollWidth reports the full text extent even while
        // visually truncated, letting a squeezed column ask for its real width.
        const contentEl = (cell as HTMLElement).querySelector<HTMLElement>(
          '[data-slot="cell-content"]'
        );
        const width = Math.max(
          contentEl?.scrollWidth ?? 0,
          (cell as HTMLElement).scrollWidth
        );
        const current = measured.get(key) ?? 0;
        if (width > current) {
          measured.set(key, width);
        }
      });
    });

    const nextWidths: Record<string, number> = {};
    visibleColumns.forEach((column) => {
      const key = String(column.key);
      const measuredWidth = measured.get(key);
      if (!measuredWidth) return;

      nextWidths[key] = Math.ceil(
        clampMeasuredWidth(column, measuredWidth + 12)
      );
    });

    setMeasuredColumnWidths((current) => {
      const currentKeys = Object.keys(current);
      const nextKeys = Object.keys(nextWidths);

      if (currentKeys.length !== nextKeys.length) {
        return nextWidths;
      }

      for (const key of nextKeys) {
        if (current[key] !== nextWidths[key]) {
          return nextWidths;
        }
      }

      return current;
    });
  }, [
    visibleColumns,
    isListMode,
    displayData,
    currentPage,
    searchQuery,
    sortConfig?.key,
    sortConfig?.direction,
    resizable,
  ]);

  // Header title and actions
  const hasHeader = Boolean(
    (title && String(title).length > 0) ??
    (Array.isArray(headerActions) ? headerActions.length > 0 : headerActions) ??
    searchable
  );

  // Selection handlers
  const handleToggleRow = (index: number) => {
    toggleRow(index);
    if (onSelectionChange) {
      const newSelection = new Set(selectedRows);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      onSelectionChange(Array.from(newSelection));
    }
  };

  const handleToggleAll = () => {
    toggleAll();
    if (onSelectionChange) {
      if (isAllSelected) {
        onSelectionChange([]);
      } else {
        const startIndex = (currentPage - 1) * pageSize;
        const allIndices = displayData.map((_, idx) => startIndex + idx);
        onSelectionChange(allIndices);
      }
    }
  };

  // Format value based on column configuration
  const formatValue = (
    value: unknown,
    column: DataGridColumnDef<T>
  ): string => {
    if (value == null) return '';

    switch (column.format) {
      case 'date': {
        const dateValue =
          value instanceof Date ? value : new Date(value as string | number);
        return new Intl.DateTimeFormat(
          'en-US',
          column.formatOptions as Intl.DateTimeFormatOptions
        ).format(dateValue);
      }

      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          ...column.formatOptions,
        } as Intl.NumberFormatOptions).format(Number(value));

      case 'number':
        return new Intl.NumberFormat(
          'en-US',
          column.formatOptions as Intl.NumberFormatOptions
        ).format(Number(value));

      case 'percent':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          ...column.formatOptions,
        } as Intl.NumberFormatOptions).format(Number(value));

      default:
        return String(value);
    }
  };

  const renderCellContent = (
    value: unknown,
    column: DataGridColumnDef<T>,
    row: T,
    rowIndex: number
  ): ReactNode => {
    const isEmpty = value == null || value === '';

    if (isEmpty && column.fallback !== undefined) {
      return column.fallback;
    }

    if (column.render) {
      return column.render(value as T[keyof T], row, rowIndex);
    }

    if (column.badgeMap) {
      const stringValue = String(value);
      const badgeConfig = column.badgeMap[stringValue] ||
        column.badgeMap['*'] || { variant: 'neutral' };

      return (
        <Badge
          variant={badgeConfig.variant ?? 'neutral'}
          size={column.componentSize ?? 'md'}
        >
          {badgeConfig.label ?? stringValue}
        </Badge>
      );
    }

    if (column.statusDotMap) {
      const stringValue = String(value);
      const dotConfig = column.statusDotMap[stringValue] ||
        column.statusDotMap['*'] || { color: 'neutral' };

      return (
        <StatusIndicator variant={dotConfig.color ?? 'neutral'}>
          {dotConfig.label ?? stringValue}
        </StatusIndicator>
      );
    }

    if (column.renderAsBadges) {
      if (Array.isArray(value)) {
        if (value.length === 0 && column.fallback !== undefined) {
          return column.fallback;
        }

        return (
          <div className="flex flex-row flex-wrap gap-2 sm:gap-1">
            {value.map((item, idx) => (
              <Badge
                key={idx}
                variant={column.badgeVariant ?? 'neutral'}
                size={column.componentSize ?? 'md'}
              >
                {String(item)}
              </Badge>
            ))}
          </div>
        );
      } else {
        return (
          <Badge
            variant={column.badgeVariant ?? 'neutral'}
            size={column.componentSize ?? 'md'}
          >
            {String(value)}
          </Badge>
        );
      }
    }

    if (column.format) {
      return formatValue(value, column);
    }

    return String(value ?? '');
  };

  const wrapInteractiveCellContent = (
    content: ReactNode,
    column: DataGridColumnDef<T>,
    hasHref: boolean
  ): ReactNode => {
    if (!hasHref || !column.interactive || !React.isValidElement(content)) {
      return content;
    }

    return (
      <span data-interactive className="relative z-10">
        {content}
      </span>
    );
  };

  // Render sort indicator
  const renderSortIcon = (columnKey: keyof T) => {
    if (!sortable) return null;

    const isSorted = sortConfig?.key === columnKey;

    if (!isSorted) {
      return (
        <ChevronsUpDown
          size={getIconPixels('xs', 'desktop')}
          className="text-neutral"
        />
      );
    }

    if (sortConfig?.direction === 'asc') {
      return (
        <ChevronUp
          size={getIconPixels('xs', 'desktop')}
          className="text-primary"
        />
      );
    }

    return (
      <ChevronDown
        size={getIconPixels('xs', 'desktop')}
        className="text-primary"
      />
    );
  };

  // Render actions for a row
  const renderActions = (row: T, actualIndex: number) => {
    if (!actions) return null;

    let rowActions: DataGridActionItem<T>[] | ReactNode;

    if (Array.isArray(actions)) {
      rowActions = actions;
    } else {
      const result = actions(row, actualIndex);
      if (Array.isArray(result)) {
        rowActions = result;
      } else {
        return <div data-interactive>{result}</div>;
      }
    }

    return (
      <div className="flex flex-row gap-3 sm:gap-2" data-interactive>
        {(rowActions as DataGridActionItem<T>[]).map((action, actionIndex) => {
          // Type guard: check if this is a DataGridAction (has onClick) vs a ReactNode
          // DataGridAction must be object with onClick function
          const isAction = (obj: unknown): obj is DataGridAction<T> =>
            typeof obj === 'object' &&
            obj !== null &&
            !React.isValidElement(obj) &&
            'onClick' in obj &&
            typeof (obj as Record<string, unknown>).onClick === 'function';

          if (isAction(action)) {
            const actionConfig = action;
            const ActionIcon = actionConfig.icon;
            const button = (
              <Button
                key={actionIndex}
                variant="ghost"
                size="icon-xs"
                aria-label={actionConfig.label}
                aria-disabled={actionConfig.disabled || undefined}
                onClick={() => {
                  if (!actionConfig.disabled)
                    actionConfig.onClick(row, actualIndex);
                }}
              >
                <ActionIcon />
              </Button>
            );

            return actionConfig.tooltip ? (
              <TooltipProvider key={actionIndex}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center">{button}</span>
                  </TooltipTrigger>
                  <TooltipContent>{actionConfig.tooltip}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              button
            );
          } else {
            return (
              <div key={actionIndex} data-interactive>
                {action as ReactNode}
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Row padding classes
  const rowPadding = compact
    ? `py-2 ${isListMode ? 'px-0' : 'px-3'}`
    : `py-3 ${isListMode ? 'px-0' : 'px-4'}`;

  const contentInsetClass =
    contentInset === 'md' ? 'pl-4' : contentInset === 'sm' ? 'pl-2' : '';

  const actionInsetClass =
    actionInset === 'md'
      ? 'pr-4'
      : actionInset === 'sm'
        ? 'pr-2'
        : actionInset === 'xs'
          ? 'pr-1'
          : '';

  const getColumnSpacingClass = (spacing?: string): string => {
    switch (spacing) {
      case 'lg':
        return 'pr-6';
      case 'md':
        return 'pr-4';
      case 'sm':
        return 'pr-2';
      case 'xs':
        return 'pr-1';
      case 'none':
      default:
        return '';
    }
  };

  const getColumnSpacingPixels = (spacing?: string): number => {
    switch (spacing) {
      case 'lg':
        return 24; // pr-6 = 24px
      case 'md':
        return 16; // pr-4 = 16px
      case 'sm':
        return 8; // pr-2 = 8px
      case 'xs':
        return 4; // pr-1 = 4px
      case 'none':
      default:
        return 0;
    }
  };

  const getMinimumFromSpacing = (spacing?: string): number => {
    switch (spacing) {
      case 'lg':
        return 250;
      case 'md':
        return 200;
      case 'sm':
        return 150;
      case 'xs':
        return 120;
      case 'none':
      default:
        return 100;
    }
  };

  // Build grid template columns for desktop layout
  const buildGridTemplateColumns = () => {
    const parts: string[] = [];

    // Selection checkbox column - fixed width for consistency across rows
    if (selectable) {
      parts.push('24px');
    }

    // Data columns
    const dataColumnParts: string[] = [];

    // When resizable with initialized widths, emit pure px tracks.
    if (columnWidthsReady) {
      visibleColumns.forEach((_, idx) => {
        dataColumnParts.push(`${columnWidths[idx]}px`);
      });
    } else {
      visibleColumns.forEach((col) => {
        let columnTemplate: string;

        if (col.width) {
          columnTemplate = col.width.trim();
        } else if (col.minWidth && col.maxWidth) {
          columnTemplate = `minmax(${col.minWidth}px, ${col.maxWidth}px)`;
        } else if (col.minWidth) {
          columnTemplate = `minmax(${col.minWidth}px, 1fr)`;
        } else if (col.maxWidth) {
          columnTemplate = `minmax(0, ${col.maxWidth}px)`;
        } else {
          columnTemplate = '1fr';
        }

        dataColumnParts.push(columnTemplate);
      });
    }

    // When resizable with fixed widths, skip measured-width refinement.
    // Otherwise apply measured widths as preferred sizes in minmax().
    const finalDataColumnParts = columnWidthsReady
      ? dataColumnParts
      : dataColumnParts.map((track, idx) => {
          const col = visibleColumns[idx];
          if (!col) return track;

          if (col.width) {
            return track;
          }

          const measuredWidth = measuredColumnWidths[String(col.key)];
          if (!measuredWidth) {
            return track;
          }

          const spacingPixels = getColumnSpacingPixels(col.spacing);
          const totalWidth = measuredWidth + spacingPixels;
          const minFloor = col.minWidth ?? getMinimumFromSpacing(col.spacing);
          const finalMax = Math.max(minFloor, totalWidth);
          return `minmax(${minFloor}px, ${finalMax}px)`;
        });

    parts.push(...finalDataColumnParts);

    const hasFlexibleFinalTrack = finalDataColumnParts.some((track) =>
      track.includes('1fr')
    );
    const needsActionsSpacer = Boolean(actions && !hasFlexibleFinalTrack);

    // Keep content-sized columns packed to the left and use an explicit spacer
    // to push actions to the far right when no data column is already flexible.
    if (needsActionsSpacer) {
      parts.push('1fr');
    }

    // Actions column - dynamically sized based on max actions across all rows
    // Use a fixed width so header and row grids resolve to identical track sizes.
    if (actions) {
      parts.push(`${actionsMinWidth}px`);
    }

    return {
      gridTemplateColumns: parts.join(' '),
      needsActionsSpacer,
    };
  };

  const { gridTemplateColumns, needsActionsSpacer } = !isListMode
    ? buildGridTemplateColumns()
    : { gridTemplateColumns: undefined, needsActionsSpacer: false };

  // Render a single row
  const renderRow = (row: T, rowIndex: number) => {
    const actualIndex = paginated
      ? (currentPage - 1) * pageSize + rowIndex
      : rowIndex;
    const isSelected = isRowSelected(actualIndex);
    const href = rowHref?.(row, actualIndex);
    const hasHref = Boolean(href);
    const hasOnRowClick = Boolean(onRowClick);
    const isClickable = hasHref || hasOnRowClick;

    // Row styling - add relative for Link overlay positioning
    const rowClasses = [
      'relative',
      'border-b border-border/50',
      striped && rowIndex % 2 === 1 ? 'bg-muted/30' : '',
      hoverable && isClickable ? 'hover:bg-neutral/4 transition-colors' : '',
      isSelected ? 'bg-primary/5' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Grid mode: CSS Grid for consistent column widths
    // List mode: vertical flex (fallback) or custom renderListItem
    const layoutClasses = isListMode
      ? 'flex flex-col gap-3'
      : `grid items-center ${gridGapClass}`;

    // Grid style for grid mode only
    const rowStyle =
      !isListMode && gridTemplateColumns ? { gridTemplateColumns } : undefined;

    // ----------------------------------------------------------------
    // List mode with custom render – delegate layout to renderListItem
    // ----------------------------------------------------------------
    if (isListMode && renderListItem) {
      const actionsContent = renderActions(row, actualIndex);
      const wrappedActions = actionsContent ? (
        <div className="mb-auto shrink-0">{actionsContent}</div>
      ) : null;
      const listItemContent = renderListItem(row, actualIndex, {
        actions: wrappedActions,
        isSelected,
        onToggleSelect: selectable
          ? () => handleToggleRow(actualIndex)
          : undefined,
      });

      // Pointer-events wrapper mirrors the grid row behaviour
      const pointerClass = hasHref ? INTERACTIVE_POINTER_CLASS : '';

      const listRowClickProps =
        !hasHref && hasOnRowClick
          ? {
              onClick: (e: React.MouseEvent<HTMLDivElement>) => {
                if (
                  isInteractiveElement(e.target as HTMLElement, e.currentTarget)
                )
                  return;
                onRowClick?.(row, actualIndex);
              },
              onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick?.(row, actualIndex);
                }
              },
              role: 'button' as const,
              tabIndex: 0,
              className:
                'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset',
            }
          : {};

      return (
        <div
          key={actualIndex}
          className={`relative ${rowPadding} ${rowClasses} ${listRowClickProps.className ?? ''}`}
          onClick={listRowClickProps.onClick}
          onKeyDown={listRowClickProps.onKeyDown}
          role={listRowClickProps.role}
          tabIndex={listRowClickProps.tabIndex}
        >
          {hasHref && href ? (
            <>
              <Link
                to={href}
                className="focus-visible:ring-primary/40 absolute inset-0 z-1 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                aria-label="View details"
                tabIndex={0}
              />
              {/* relative z-10 lifts content above the link overlay; pointer-events selectively re-enabled on interactive descendants */}
              <div className={`relative z-10 ${pointerClass}`}>
                {listItemContent}
              </div>
            </>
          ) : (
            listItemContent
          )}
        </div>
      );
    }

    // ----------------------------------------------------------------
    // Props for onRowClick (when no href) - handlers are on the row div
    // Uses isInteractiveElement check to avoid triggering on links/buttons
    const rowClickProps =
      !hasHref && hasOnRowClick
        ? {
            onClick: (e: React.MouseEvent<HTMLDivElement>) => {
              // Don't trigger if clicked on an interactive element (links, buttons, etc.)
              if (
                isInteractiveElement(e.target as HTMLElement, e.currentTarget)
              ) {
                return;
              }
              onRowClick?.(row, actualIndex);
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onRowClick?.(row, actualIndex);
              }
            },
            role: 'button' as const,
            tabIndex: 0,
            className:
              'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset',
          }
        : {};

    return (
      <div
        key={actualIndex}
        className={`${layoutClasses} ${rowPadding} ${rowClasses} ${rowClickProps.className ?? ''}`}
        style={rowStyle}
        onClick={rowClickProps.onClick}
        onKeyDown={rowClickProps.onKeyDown}
        role={rowClickProps.role}
        tabIndex={rowClickProps.tabIndex}
      >
        {/* Row link overlay - positioned behind content */}
        {hasHref && href && (
          <Link
            to={href}
            className="focus-visible:ring-primary/40 absolute inset-0 z-1 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
            aria-label={`View details`}
            tabIndex={0}
          />
        )}

        {/* Selection checkbox - above link layer */}
        {selectable && (
          <div className={`relative z-10 ${isListMode ? '' : 'shrink-0'}`}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => handleToggleRow(actualIndex)}
              aria-label={`Select row ${actualIndex + 1}`}
            />
          </div>
        )}

        {/* Data cells - pointer-events-none lets clicks pass through to link layer */}
        {/* Interactive elements inside (links, buttons) keep their default pointer-events */}
        {visibleColumns.map((column) => {
          const value = row[column.key];
          const content = wrapInteractiveCellContent(
            renderCellContent(value, column, row, actualIndex),
            column,
            hasHref
          );
          const showLabel = isListMode && column.showLabelOnMobile !== false;

          // Alignment
          const alignClass =
            column.align === 'center'
              ? 'text-center'
              : column.align === 'right'
                ? 'text-right'
                : 'text-left';
          const contentWrapperClass =
            column.align === 'center'
              ? 'w-full flex justify-center'
              : column.align === 'right'
                ? 'w-full flex justify-end'
                : 'w-full';

          // Only disable pointer events when we have a link overlay
          const pointerClass = hasHref ? INTERACTIVE_POINTER_CLASS : '';

          return (
            <div
              key={String(column.key)}
              data-col-key={String(column.key)}
              className={`body ${alignClass} ${isListMode ? 'flex flex-col gap-0.5' : 'min-w-0 overflow-hidden'} ${pointerClass} ${getColumnSpacingClass(column.spacing)}`}
            >
              {showLabel && (
                <Text
                  size="xs"
                  variant="muted"
                  className={`tracking-wide uppercase ${alignClass}`}
                >
                  {column.label}
                </Text>
              )}
              <div className={isListMode ? '' : contentWrapperClass}>
                <div
                  data-slot="cell-content"
                  className={`${column.wrapText ? '' : 'truncate'} ${alignClass}`}
                  title={
                    !column.wrapText &&
                    (typeof value === 'string' || typeof value === 'number')
                      ? String(value)
                      : undefined
                  }
                >
                  {content}
                </div>
              </div>
            </div>
          );
        })}

        {needsActionsSpacer && <div aria-hidden="true" />}

        {/* Actions - above link layer */}
        {actions && (
          <div
            className={`relative z-10 shrink-0 ${isListMode ? 'mb-auto flex justify-end pt-1' : 'justify-self-end'}`}
          >
            {renderActions(row, actualIndex)}
          </div>
        )}
      </div>
    );
  };

  const grid = (
    <div
      ref={containerRef}
      className={`w-full ${resizable && !stickyHeader ? 'overflow-x-auto' : stickyHeader ? 'overflow-x-clip' : 'overflow-hidden'} ${contentInsetClass} ${actionInsetClass} ${card ? 'p-4 sm:p-6' : ''} ${card ? '' : className}`}
    >
      {/* Header with title, search, actions */}
      {hasHeader && (
        <div className="mb-3">
          {searchable ? (
            <div className="flex flex-col gap-3">
              {title && (
                <h2 className="text-foreground text-lg font-semibold">
                  {title}
                </h2>
              )}
              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 sm:max-w-md">
                  <InputGroup className="w-full">
                    <InputGroupInput
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder={searchPlaceholder}
                    />
                    <InputGroupAddon>
                      <Search size={getIconPixels('sm', 'desktop')} />
                    </InputGroupAddon>
                    {searchQuery && (
                      <InputGroupAddon align="inline-end">
                        <InputGroupButton
                          size="icon-xs"
                          aria-label="Clear search"
                          onClick={() => handleSearchChange('')}
                        >
                          <X />
                        </InputGroupButton>
                      </InputGroupAddon>
                    )}
                  </InputGroup>
                </div>

                {Array.isArray(headerActions) && headerActions.length > 0 && (
                  <div className="ml-auto flex flex-wrap items-center gap-2 sm:flex-nowrap">
                    {headerActions.map((act, idx) =>
                      renderHeaderAction(act, idx)
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1">
                {title && (
                  <h2 className="text-foreground text-lg font-semibold">
                    {title}
                  </h2>
                )}
              </div>

              {Array.isArray(headerActions) && headerActions.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  {headerActions.map((act, idx) =>
                    renderHeaderAction(act, idx)
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty states */}
      {data.length === 0 ? (
        <div className={card ? '' : 'border-border rounded-lg border'}>
          <EmptyState
            title={emptyState.title}
            description={emptyState.description}
            buttonText={emptyState.buttonText}
            onButtonClick={emptyState.onButtonClick}
            buttonTo={emptyState.buttonTo}
            buttonHref={emptyState.buttonHref}
            buttonTarget={emptyState.buttonTarget}
            buttonRel={emptyState.buttonRel}
          />
        </div>
      ) : filteredData.length === 0 && searchQuery.trim() ? (
        <div className={card ? '' : 'border-border rounded-lg border'}>
          <EmptyState
            title="No results found"
            description={`No items match "${searchQuery}". Try adjusting your search.`}
          />
        </div>
      ) : (
        <>
          {/* Column headers – grid mode only (hidden in list mode) */}
          {!isListMode && (
            <div
              className={`grid items-center ${gridGapClass} ${rowPadding} border-border border-b${stickyHeader ? ' bg-muted sticky top-0 z-20' : ' bg-muted/30'}`}
              style={gridTemplateColumns ? { gridTemplateColumns } : undefined}
            >
              {selectable && (
                <div>
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleToggleAll}
                    aria-label="Select all rows"
                  />
                </div>
              )}

              {visibleColumns.map((column, colIdx) => {
                const alignClass =
                  column.align === 'center'
                    ? 'text-center'
                    : column.align === 'right'
                      ? 'text-right'
                      : 'text-left';
                const headerContentClass =
                  column.align === 'center'
                    ? 'w-full flex justify-center'
                    : column.align === 'right'
                      ? 'w-full flex justify-end'
                      : 'w-full';
                const isColResizable = column.resizable ?? resizable;

                return (
                  <div
                    key={colIdx}
                    data-col-key={String(column.key)}
                    data-col-idx={colIdx}
                    className={`header min-w-0 ${isColResizable ? 'relative' : ''} ${alignClass} ${getColumnSpacingClass(column.spacing)}`}
                  >
                    <div className={headerContentClass}>
                      {column.sortable !== false && sortable ? (
                        <button
                          onClick={() => handleSort(column.key)}
                          className="hover:text-foreground text-muted-foreground m-0 inline-flex items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium transition-colors"
                          type="button"
                        >
                          {column.label}
                          {renderSortIcon(column.key)}
                        </button>
                      ) : (
                        <Text size="sm" variant="muted" weight="medium">
                          {column.label}
                        </Text>
                      )}
                    </div>
                    {isColResizable && colIdx < visibleColumns.length - 1 && (
                      <div
                        className="group/resize absolute top-0 -right-1.5 z-20 h-full w-3 cursor-col-resize"
                        onMouseDown={(e) => handleResizeStart(colIdx, e)}
                      >
                        <div className="bg-border group-hover/resize:bg-primary/60 group-active/resize:bg-primary mx-auto h-full w-px transition-colors" />
                      </div>
                    )}
                  </div>
                );
              })}

              {needsActionsSpacer && <div aria-hidden="true" />}

              {actions && (
                <div className="justify-self-end">
                  <Text size="sm" variant="muted" weight="medium">
                    {actionsLabel}
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Select all checkbox (list mode fallback only – renderListItem handles its own selection UI) */}
          {isListMode && !renderListItem && selectable && (
            <div
              className={`flex items-center gap-2 ${rowPadding} border-border border-b`}
            >
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={handleToggleAll}
                aria-label="Select all rows"
              />
              <Text size="sm" variant="muted">
                Select all
              </Text>
            </div>
          )}

          {/* When stickyHeader+resizable the rows and pagination live in their own
              overflow-x-auto wrapper so the sticky header (outside this wrapper)
              is not trapped inside a new scroll container. */}
          {stickyHeader && resizable ? (
            <div className="overflow-x-auto">
              <div className="divide-border/50 divide-y">
                {displayData.map((row, idx) => renderRow(row, idx))}
              </div>

              {/* Pagination controls — client-side */}
              {paginated && !serverPagination && totalPages > 1 && (
                <div className="border-border flex items-center justify-between border-t px-4 py-3">
                  <div className="text-muted-foreground text-sm">
                    Page {currentPage} of {totalPages} ({filteredData.length}{' '}
                    {searchQuery.trim() ? 'filtered' : 'total'} rows
                    {searchQuery.trim() && data.length !== filteredData.length
                      ? ` of ${data.length}`
                      : ''}
                    )
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={prevPage}
                      disabled={!canPrevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={nextPage}
                      disabled={!canNextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              {/* Pagination controls — server-side */}
              {serverPagination &&
                (serverPagination.canPrevPage ||
                  serverPagination.canNextPage) && (
                  <div className="border-border flex items-center justify-between border-t px-4 py-3">
                    <div className="text-muted-foreground text-sm">
                      {serverPagination.pageLabel ?? `${data.length} items`}
                      {serverPagination.isFetching ? '' : ''}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={serverPagination.onPrevPage}
                        disabled={
                          !serverPagination.canPrevPage ||
                          serverPagination.isFetching
                        }
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={serverPagination.onNextPage}
                        disabled={
                          !serverPagination.canNextPage ||
                          serverPagination.isFetching
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <>
              {/* Rows */}
              <div className="divide-border/50 divide-y">
                {displayData.map((row, idx) => renderRow(row, idx))}
              </div>

              {/* Pagination controls — client-side */}
              {paginated && !serverPagination && totalPages > 1 && (
                <div className="border-border flex items-center justify-between border-t px-4 py-3">
                  <div className="text-muted-foreground text-sm">
                    Page {currentPage} of {totalPages} ({filteredData.length}{' '}
                    {searchQuery.trim() ? 'filtered' : 'total'} rows
                    {searchQuery.trim() && data.length !== filteredData.length
                      ? ` of ${data.length}`
                      : ''}
                    )
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={prevPage}
                      disabled={!canPrevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={nextPage}
                      disabled={!canNextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              {/* Pagination controls — server-side */}
              {serverPagination &&
                (serverPagination.canPrevPage ||
                  serverPagination.canNextPage) && (
                  <div className="border-border flex items-center justify-between border-t px-4 py-3">
                    <div className="text-muted-foreground text-sm">
                      {serverPagination.pageLabel ?? `${data.length} items`}
                      {serverPagination.isFetching ? '' : ''}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={serverPagination.onPrevPage}
                        disabled={
                          !serverPagination.canPrevPage ||
                          serverPagination.isFetching
                        }
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={serverPagination.onNextPage}
                        disabled={
                          !serverPagination.canNextPage ||
                          serverPagination.isFetching
                        }
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
            </>
          )}
        </>
      )}
    </div>
  );

  if (card) {
    return <Card className={`w-full ${className ?? ''}`}>{grid}</Card>;
  }

  return grid;
}

DataGrid.displayName = 'DataGrid';

export default DataGrid;
